import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';
import { duas, duaCategories } from '../data/duasData';
import Footer from '../components/layout/Footer';

const {
    FiHeart, FiSearch, FiCopy, FiShare2, FiEdit3,
    FiFolder, FiCheck, FiX,FiGrid, FiPlus, FiTrash2,
    FiPlay, FiPause, FiSkipForward, FiEdit
} = FiIcons;

const Duas = () => {
    const {
        user, addFavorite, addNote, updateDuaProgress,
        addCategory, addItemToCategory, addCustomDua, updateCustomDua, deleteCustomDua,
        isAdmin
    } = useAuth();

    // Filters & Search
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Settings
    const [fontSize, setFontSize] = useState(user?.preferences?.fontSize || 'medium');
    const [translation, setTranslation] = useState(user?.preferences?.translation || 'english');

    // Audio System State
    const [currentAudio, setCurrentAudio] = useState(null); // { id, src }
    const [isPlaying, setIsPlaying] = useState(false);
    const [playQueue, setPlayQueue] = useState([]); // List of dua IDs to play sequentially
    const [isQueuePlaying, setIsQueuePlaying] = useState(false);
    const audioRef = useRef(new Audio());

    // Interactive States
    const [activeNoteDua, setActiveNoteDua] = useState(null);
    const [noteContent, setNoteContent] = useState('');
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [activeCategoryDua, setActiveCategoryDua] = useState(null);
    const [newCategoryName, setNewCategoryName] = useState('');

    // Add/Edit Dua Modal State
    const [showAddDuaModal, setShowAddDuaModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingDuaId, setEditingDuaId] = useState(null);
    const [newDua, setNewDua] = useState({
        title: '',
        arabic: '',
        transliteration: '',
        translation: '',
        reference: '',
        benefits: '',
        category: 'morning',
        audio: ''
    });

    // Sync preferences
    useEffect(() => {
        if (user?.preferences) {
            if (user.preferences.fontSize) setFontSize(user.preferences.fontSize);
            if (user.preferences.translation) setTranslation(user.preferences.translation);
        }
    }, [user]);

    const fontSizes = {
        small: 'text-lg',
        medium: 'text-2xl',
        large: 'text-3xl',
        xlarge: 'text-4xl'
    };

    const getIcon = (iconName) => {
        return FiIcons[iconName] || FiGrid;
    };

    // Merge static duas with user's custom duas
    const allDuas = [...duas, ...(user?.customDuas || [])];

    const getCategoryCount = (categoryId) => {
        return allDuas.filter(d => d.category === categoryId).length;
    };

    const filteredDuas = allDuas.filter(dua => {
        let matchesCategory = false;

        // Check for user-created category first
        const userCategory = user?.categories?.find(c => c.id.toString() === selectedCategory);

        if (selectedCategory === 'all') {
            matchesCategory = true;
        } else if (selectedCategory === 'custom') {
            matchesCategory = dua.isCustom;
        } else if (userCategory) {
            const targetId = `dua-${dua.id}`;
            matchesCategory = userCategory.items?.some(item => item.id === targetId);
        } else {
            matchesCategory = dua.category === selectedCategory;
        }

        const matchesSearch =
            dua.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (dua.translations && dua.translations[translation]?.toLowerCase().includes(searchTerm.toLowerCase()));
        return matchesCategory && matchesSearch;
    });

    // --- Audio Logic ---
    const togglePlay = (dua) => {
        if (!dua.audio) return;

        if (currentAudio?.id === dua.id) {
            if (isPlaying) {
                audioRef.current.pause();
                setIsPlaying(false);
                setIsQueuePlaying(false);
            } else {
                audioRef.current.play().catch(e => {
                    console.error("Resume playback failed", e);
                });
                setIsPlaying(true);
            }
        } else {
            playDua(dua);
        }
    };

    const playDua = async (dua) => {
        if (!dua.audio) return;

        try {
            audioRef.current.pause();
            audioRef.current.src = dua.audio;
            setCurrentAudio({ id: dua.id, src: dua.audio });
            setIsPlaying(true);

            await audioRef.current.play();
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error("Audio playback error:", error);
                toast.error("Could not play audio");
            }
            if (audioRef.current.src === dua.audio) {
                setIsPlaying(false);
            }
        }
    };

    const handlePlayAll = () => {
        const playlist = filteredDuas.filter(d => d.audio);
        if (playlist.length === 0) {
            toast.error("No audio available in this list");
            return;
        }

        setPlayQueue(playlist);
        setIsQueuePlaying(true);
        playDua(playlist[0]);
        toast.success(`Playing ${playlist.length} duas`);
    };

    useEffect(() => {
        const audio = audioRef.current;

        const handleEnded = () => {
            setIsPlaying(false);

            if (isQueuePlaying && playQueue.length > 0) {
                const currentIndex = playQueue.findIndex(d => d.id === currentAudio?.id);
                if (currentIndex !== -1 && currentIndex < playQueue.length - 1) {
                    const nextDua = playQueue[currentIndex + 1];
                    playDua(nextDua);
                } else {
                    setIsQueuePlaying(false);
                    toast("Playlist finished");
                }
            }
        };

        const handleError = (e) => {
            console.error("Audio Error Event:", e);
            setIsPlaying(false);
            setIsQueuePlaying(false);
        };

        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);

        return () => {
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
        };
    }, [currentAudio, isQueuePlaying, playQueue]);

    useEffect(() => {
        const audio = audioRef.current;

        return () => {
            if (audio) {
                audio.pause();
                audio.src = '';
            }
        };
    }, []);

    // --- Actions ---
    const handleFavorite = (dua) => {
        if (!user) return toast.error('Please sign in');
        addFavorite({
            id: `dua-${dua.id}`,
            type: 'dua',
            title: dua.title,
            text: dua.arabic,
            translation: dua.translations ? (dua.translations[translation] || dua.translations['english']) : dua.translation,
            reference: dua.reference,
            date: new Date().toISOString()
        });
        updateDuaProgress('favorites', `dua-${dua.id}`);
        toast.success('Added to favorites');
    };

    const handleSaveNote = () => {
        if (!noteContent.trim()) return;
        addNote({
            id: Date.now(),
            type: 'dua',
            reference: activeNoteDua.reference || activeNoteDua.title,
            content: noteContent,
            date: new Date().toISOString()
        });
        toast.success('Note saved');
        setActiveNoteDua(null);
        setNoteContent('');
        updateDuaProgress('completed', `dua-${activeNoteDua.id}`);
    };

    const handleCopy = (dua) => {
        const t = dua.translations ? (dua.translations[translation] || dua.translations['english']) : dua.translation;
        const parts = [
            dua.title,
            dua.arabic,
            dua.transliteration,
            t,
            dua.reference ? `Reference: ${dua.reference}` : '',
            dua.benefits ? `Benefits: ${dua.benefits}` : ''
        ].filter(Boolean);

        const text = parts.join('\n\n');
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const handleShare = (dua) => {
        const text = `Read this Dua: ${dua.title}\n${dua.translations ? (dua.translations[translation] || dua.translations['english']) : dua.translation}`;
        const url = window.location.href;
        const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        window.open(shareUrl, '_blank');
    };

    const handleAddToCategory = async () => {
        if (!activeCategoryDua || !newCategoryName.trim()) return;
        try {
            const response = await addCategory(newCategoryName);
            const newCategoryId = response.data.id; // assuming API returns { id: ..., name: ... }

            // 3️⃣ Call your existing function to save Hadith to this new category
            handleSaveToExistingCategory(newCategoryId);

            // 4️⃣ UI updates
            toast.success(
                `Category "${newCategoryName}" created. Hadith saved to it!`
            );

            setNewCategoryName('');
            setShowCategoryModal(false);

        } catch (error) {
            console.error("Category creation failed:", error);
            toast.error(
                error.response?.data?.message || "Failed to create category"
            );
        }
    };

    const handleSaveToExistingCategory = (categoryId) => {
        if (!activeCategoryDua) return;
        addItemToCategory(categoryId, {
            id: `dua-${activeCategoryDua.id}`,
            type: 'dua',
            ref: activeCategoryDua.title,
            content: activeCategoryDua.translations ? (activeCategoryDua.translations[translation] || activeCategoryDua.translations['english']) : activeCategoryDua.translation
        });
        toast.success('Dua saved to category');
        setShowCategoryModal(false);
        setActiveCategoryDua(null);
    };

    const openAddDuaModal = () => {
        setIsEditing(false);
        setEditingDuaId(null);
        setNewDua({
            title: '',
            arabic: '',
            transliteration: '',
            translation: '',
            reference: '',
            benefits: '',
            category: 'morning',
            audio: ''
        });
        setShowAddDuaModal(true);
    };

    const openEditDuaModal = (dua) => {
        setIsEditing(true);
        setEditingDuaId(dua.id);
        setNewDua({
            title: dua.title || '',
            arabic: dua.arabic || '',
            transliteration: dua.transliteration || '',
            translation: dua.translations ? (dua.translations['english'] || dua.translation) : (dua.translation || ''),
            reference: dua.reference || '',
            benefits: dua.benefits || '',
            category: dua.category || 'morning',
            audio: dua.audio || ''
        });
        setShowAddDuaModal(true);
    };

    const handleAddDuaSubmit = () => {
        if (!newDua.title || !newDua.translation) {
            toast.error('Title and Translation are required');
            return;
        }

        if (isEditing) {
            const originalDua = allDuas.find(d => d.id === editingDuaId);

            if (originalDua && originalDua.isCustom) {
                updateCustomDua(editingDuaId, newDua);
                toast.success('Dua Updated!');
            } else {
                addCustomDua(newDua);
                toast.success('Static Dua saved as new Custom Dua');
            }
        } else {
            addCustomDua(newDua);
            toast.success('Custom Dua Added!');
        }

        setShowAddDuaModal(false);
        setNewDua({
            title: '',
            arabic: '',
            transliteration: '',
            translation: '',
            reference: '',
            benefits: '',
            category: 'morning',
            audio: ''
        });
    };

    const handleDeleteCustomDua = (id) => {
        if (window.confirm("Are you sure you want to delete this custom dua?")) {
            deleteCustomDua(id);
            toast.success("Dua deleted");
        }
    }

    return (
        <div className="min-h-screen pt-8 bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* Header & Controls */}
                <div className="flex flex-col lg:flex-row justify-between items-center gap-4 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm sticky top-20 z-10">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Islamic Duas</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Authentic supplications from Quran & Sunnah</p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto items-center flex-wrap justify-end">
                        {/* Search */}
                        <div className="relative w-full sm:w-auto min-w-[200px]">
                            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search duas..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-4 py-2 w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                            />
                        </div>

                        {/* Category Dropdown */}
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="px-3 py-2 w-full sm:w-auto bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                        >
                            <option value="all">All Categories</option>
                            {user?.customDuas?.length > 0 && <option value="custom">Custom Duas</option>}

                            {user?.categories?.length > 0 && (
                                <optgroup label="My Collections">
                                    {user.categories
                                        .filter(cat => cat.items.some(item => item.item_type === "dua"))
                                        .map(cat => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.name} ({cat.items.length})
                                            </option>
                                        ))}
                                </optgroup>
                            )}
                        </select>

                        {/* Settings */}
                        <select
                            value={translation}
                            onChange={(e) => setTranslation(e.target.value)}
                            className="px-3 py-2 w-full sm:w-auto bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                        >
                            <option value="english">English</option>
                            <option value="urdu">Urdu</option>
                            <option value="hindi">Hindi</option>
                            <option value="bangla">Bangla</option>
                        </select>

                        <select
                            value={fontSize}
                            onChange={(e) => setFontSize(e.target.value)}
                            className="px-3 py-2 w-full sm:w-auto bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
                        >
                            <option value="small">Small</option>
                            <option value="medium">Medium</option>
                            <option value="large">Large</option>
                            <option value="xlarge">Extra Large</option>
                        </select>

                        <button
                            onClick={handlePlayAll}
                            disabled={filteredDuas.filter(d => d.audio).length === 0}
                            className="px-4 py-2 w-full sm:w-auto bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                            title="Play all audios in current list"
                        >
                            <SafeIcon icon={isQueuePlaying ? FiPause : FiSkipForward} />
                            {isQueuePlaying ? 'Playing All...' : 'Play All'}
                        </button>

                        <button
                            onClick={openAddDuaModal}
                            className="px-4 py-2 w-full sm:w-auto bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                        >
                            <SafeIcon icon={FiPlus} />
                            Add Dua
                        </button>
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-6">
                    {/* Categories Sidebar */}
                    <div className="lg:col-span-3 flex lg:block overflow-x-auto lg:overflow-y-auto whitespace-nowrap lg:whitespace-normal gap-2 lg:gap-0 lg:space-y-1 h-auto lg:h-[calc(100vh-200px)] sticky top-[152px] lg:top-48 z-0 custom-scrollbar pb-4 lg:pb-0">
                        <button
                            onClick={() => setSelectedCategory('all')}
                            className={`w-auto lg:w-full flex-shrink-0 flex items-center justify-between px-4 py-2 lg:px-3 lg:py-1.5 rounded-lg transition-colors border lg:border-none border-gray-100 dark:border-gray-700 ${selectedCategory === 'all'
                                    ? 'bg-emerald-600 text-white shadow-md'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-gray-700'
                                }`}
                        >
                            <div className="flex items-center gap-2">
                                <SafeIcon icon={FiGrid} className="w-5 h-5 flex-shrink-0" />
                                <span className="font-medium text-sm lg:text-base">All Duas</span>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${selectedCategory === 'all'
                                    ? 'bg-white/20 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                }`}>
                                {allDuas.length}
                            </span>
                        </button>

                        {user?.customDuas?.length > 0 && (
                            <button
                                onClick={() => setSelectedCategory('custom')}
                                className={`w-auto lg:w-full flex-shrink-0 flex items-center justify-between px-4 py-2 lg:px-3 lg:py-1.5 rounded-lg transition-colors border lg:border-none border-gray-100 dark:border-gray-700 ${selectedCategory === 'custom'
                                        ? 'bg-emerald-600 text-white shadow-md'
                                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-gray-700'
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    <SafeIcon icon={FiEdit3} className="w-5 h-5 flex-shrink-0" />
                                    <span className="font-medium text-sm lg:text-base">My Custom Duas</span>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${selectedCategory === 'custom'
                                        ? 'bg-white/20 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                    }`}>
                                    {user.customDuas.length}
                                </span>
                            </button>
                        )}

                        <div className="hidden lg:block pt-2 pb-1">
                            <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Categories</p>
                        </div>

                        {duaCategories.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                className={`w-auto lg:w-full flex-shrink-0 flex items-center justify-between px-4 py-2 lg:px-3 lg:py-1.5 rounded-lg transition-colors border lg:border-none border-gray-100 dark:border-gray-700 ${selectedCategory === cat.id
                                        ? 'bg-emerald-600 text-white shadow-md'
                                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-gray-700'
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    <SafeIcon icon={getIcon(cat.icon)} className="w-5 h-5 flex-shrink-0" />
                                    <span className="font-medium text-sm lg:text-base text-left">{cat.name}</span>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${selectedCategory === cat.id
                                        ? 'bg-white/20 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                    }`}>
                                    {getCategoryCount(cat.id)}
                                </span>
                            </button>
                        ))}

                        {user?.categories?.length > 0 && (
                            <>
                                <div className="hidden lg:block pt-4 pb-1">
                                    <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">My Collections</p>
                                </div>
                                {user.categories.map(cat => (
                                    <button
                                        key={cat.id}
                                        onClick={() => setSelectedCategory(cat.id.toString())}
                                        className={`w-auto lg:w-full flex-shrink-0 flex items-center justify-between px-4 py-2 lg:px-3 lg:py-1.5 rounded-lg transition-colors border lg:border-none border-gray-100 dark:border-gray-700 ${selectedCategory === cat.id.toString()
                                                ? 'bg-emerald-600 text-white shadow-md'
                                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-gray-700'
                                            }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <SafeIcon icon={FiFolder} className="w-5 h-5 flex-shrink-0" />
                                            <span className="font-medium text-sm lg:text-base text-left break-words">{cat.name}</span>
                                        </div>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${selectedCategory === cat.id.toString()
                                                ? 'bg-white/20 text-white'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                            }`}>
                                            {cat.items.length}
                                        </span>
                                    </button>
                                ))}
                            </>
                        )}
                    </div>

                    {/* Main Content */}
                    <div className="lg:col-span-9 space-y-6">
                        {filteredDuas.length === 0 ? (
                            <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                                <SafeIcon icon={FiSearch} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500">No duas found matching your criteria.</p>
                            </div>
                        ) : (
                            filteredDuas.map((dua) => (
                                <motion.div
                                    key={dua.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`bg-white dark:bg-gray-800 rounded-xl border transition-all relative ${currentAudio?.id === dua.id
                                            ? 'border-emerald-500 ring-1 ring-emerald-500 shadow-lg'
                                            : 'border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md'
                                        }`}
                                >
                                    <div className="p-6">
                                        {dua.isCustom && (
                                            <button
                                                onClick={() => handleDeleteCustomDua(dua.id)}
                                                className="absolute top-4 right-4 text-gray-400 hover:text-red-500 p-1"
                                                title="Delete Custom Dua"
                                            >
                                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                            </button>
                                        )}

                                        <div className="flex justify-between items-start mb-4 pr-8">
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                    {dua.title}
                                                    {currentAudio?.id === dua.id && isPlaying && (
                                                        <span className="flex h-2 w-2 relative">
                                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                                        </span>
                                                    )}
                                                </h3>
                                                <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">{dua.reference || 'Custom Dua'}</p>
                                            </div>
                                            {!dua.isCustom && (
                                                <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs rounded-full font-medium">
                                                    {duaCategories.find(c => c.id === dua.category)?.name}
                                                </span>
                                            )}
                                        </div>

                                        <div className="space-y-6 mb-6">
                                            <p className={`text-right font-arabic leading-loose text-gray-900 dark:text-white ${fontSizes.large}`} dir="rtl">
                                                {dua.arabic}
                                            </p>
                                            <p className="text-gray-600 dark:text-gray-400 italic font-medium">
                                                {dua.transliteration}
                                            </p>
                                            <p className={`text-gray-800 dark:text-gray-200 leading-relaxed ${fontSizes[fontSize]}`}>
                                                {dua.translations ? (dua.translations[translation] || dua.translations['english']) : dua.translation}
                                            </p>
                                            {dua.benefits && (
                                                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
                                                    <p className="text-sm text-blue-800 dark:text-blue-300 whitespace-pre-line">
                                                        <span className="font-bold">Benefit:</span> {dua.benefits}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Custom Audio Player UI */}
                                            {dua.audio && (
                                                <div className="mt-4 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg flex items-center gap-3 border border-gray-100 dark:border-gray-700">
                                                    <button
                                                        onClick={() => togglePlay(dua)}
                                                        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${currentAudio?.id === dua.id && isPlaying
                                                                ? 'bg-emerald-500 text-white shadow-emerald-200 shadow-lg'
                                                                : 'bg-white dark:bg-gray-600 text-emerald-600 dark:text-emerald-400 border border-gray-200 dark:border-gray-500 hover:bg-emerald-50 dark:hover:bg-gray-500'
                                                            }`}
                                                    >
                                                        <SafeIcon
                                                            icon={currentAudio?.id === dua.id && isPlaying ? FiPause : FiPlay}
                                                            className="w-4 h-4 ml-0.5"
                                                        />
                                                    </button>

                                                    <div className="flex-1">
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium">
                                                            {currentAudio?.id === dua.id && isPlaying ? 'Now Playing' : 'Audio Recitation'}
                                                        </div>
                                                        <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                                            {currentAudio?.id === dua.id && (
                                                                <motion.div
                                                                    initial={{ width: 0 }}
                                                                    animate={{ width: isPlaying ? '100%' : '0%' }}
                                                                    transition={{ duration: 30, ease: "linear", repeat: isPlaying ? Infinity : 0 }}
                                                                    className="h-full bg-emerald-500 rounded-full"
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Action Toolbar */}
                                        <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-gray-100 dark:border-gray-700">
                                            {/* Edit Button (Admin Only) - 1st Position */}
                                            {isAdmin && (
                                                <button
                                                    onClick={() => openEditDuaModal(dua)}
                                                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                                                >
                                                    <SafeIcon icon={FiEdit} className="w-4 h-4" />
                                                    <span className="hidden sm:inline">Edit</span>
                                                </button>
                                            )}

                                            {/* Favorite Button - 2nd Position */}
                                            <button
                                                onClick={() => handleFavorite(dua)}
                                                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                title="Add to Favorites"
                                            >
                                                <SafeIcon icon={FiHeart} className="w-4 h-4" />
                                                <span className="hidden sm:inline">Favorite</span>
                                            </button>

                                            {/* Note Button - 3rd Position */}
                                            <button onClick={() => { setActiveNoteDua(dua); }} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors">
                                                <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                                                <span className="hidden sm:inline">Note</span>
                                            </button>

                                            {/* Category Button - 4th Position */}
                                            <button onClick={() => { setActiveCategoryDua(dua); setShowCategoryModal(true); }} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors">
                                                <SafeIcon icon={FiFolder} className="w-4 h-4" />
                                                <span className="hidden sm:inline">Category</span>
                                            </button>

                                            {/* Copy Button - 5th Position (Moved to left with others) */}
                                            <button
                                                onClick={() => handleCopy(dua)}
                                                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                                                title="Copy Content"
                                            >
                                                <SafeIcon icon={FiCopy} className="w-4 h-4" />
                                                <span className="hidden sm:inline">Copy</span>
                                            </button>

                                            {/* Share Button - 6th Position (Last) */}
                                            <button onClick={() => handleShare(dua)} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors">
                                                <SafeIcon icon={FiShare2} className="w-4 h-4" />
                                                <span className="hidden sm:inline">Share</span>
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Note Modal */}
            <AnimatePresence>
                {activeNoteDua && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6"
                        >
                            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Add Note</h3>
                            <p className="text-sm text-gray-500 mb-4">{activeNoteDua.title}</p>
                            <textarea
                                value={noteContent}
                                onChange={(e) => setNoteContent(e.target.value)}
                                className="w-full h-32 p-3 border rounded-lg mb-4 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                placeholder="Write your reflections..."
                            />
                            <div className="flex justify-end gap-2">
                                <button onClick={() => setActiveNoteDua(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
                                <button onClick={handleSaveNote} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Save Note</button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Category Modal */}
            <AnimatePresence>
                {showCategoryModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6"
                        >
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Save to Category</h3>
                                <button onClick={() => { setShowCategoryModal(false); setActiveCategoryDua(null); }}><SafeIcon icon={FiX} className="text-gray-500" /></button>
                            </div>

                            {user?.categories && user.categories.length > 0 && (
                                <div className="mb-4 space-y-2 max-h-40 overflow-y-auto">
                                    <p className="text-xs text-gray-500 font-semibold uppercase">Existing Categories</p>
                                    {user.categories
                                    .filter(cat => cat.items.some(item => item.item_type === "dua"))
                                    .map(cat => (
                                        <button
                                        key={cat.id}
                                        onClick={() => handleSaveToExistingCategory(cat.id)}
                                        className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-sm flex justify-between"
                                        >
                                        <span className="text-gray-800 dark:text-gray-200">+ {cat.name}</span>
                                        <span className="text-gray-400 text-xs">{cat.items.length} items</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Create New Category</p>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newCategoryName}
                                        onChange={(e) => setNewCategoryName(e.target.value)}
                                        maxLength={50}
                                        placeholder="Name (max 15 chars)"
                                        className="flex-1 px-3 py-2 border rounded-lg text-sm bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    />
                                    <button
                                        onClick={handleAddToCategory}
                                        disabled={!newCategoryName.trim()}
                                        className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                                    >
                                        <SafeIcon icon={FiCheck} />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Add Custom Dua Modal */}
            <AnimatePresence>
                {showAddDuaModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
                        >
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {isEditing ? 'Edit Dua' : 'Add New Dua'}
                                </h3>
                                <button onClick={() => setShowAddDuaModal(false)}><SafeIcon icon={FiX} className="text-gray-500" /></button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
                                    <input
                                        type="text"
                                        value={newDua.title}
                                        onChange={e => setNewDua({ ...newDua, title: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        placeholder="e.g. My Morning Prayer"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Arabic (Optional)</label>
                                    <textarea
                                        value={newDua.arabic}
                                        onChange={e => setNewDua({ ...newDua, arabic: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white font-arabic text-right dir-rtl"
                                        placeholder="Arabic text..."
                                        rows={3}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Transliteration (Optional)</label>
                                    <textarea
                                        value={newDua.transliteration}
                                        onChange={e => setNewDua({ ...newDua, transliteration: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        placeholder="English pronunciation..."
                                        rows={2}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Translation *</label>
                                    <textarea
                                        value={newDua.translation}
                                        onChange={e => setNewDua({ ...newDua, translation: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        placeholder="Meaning of the dua..."
                                        rows={3}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reference (Optional)</label>
                                    <input
                                        type="text"
                                        value={newDua.reference}
                                        onChange={e => setNewDua({ ...newDua, reference: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        placeholder="e.g. Bukhari 101"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                                    <select
                                        value={newDua.category}
                                        onChange={e => setNewDua({ ...newDua, category: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    >
                                        {duaCategories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Audio URL (Optional)</label>
                                    <input
                                        type="url"
                                        value={newDua.audio}
                                        onChange={e => setNewDua({ ...newDua, audio: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        placeholder="https://example.com/audio.mp3"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Benefits/Notes (Optional)</label>
                                    <textarea
                                        value={newDua.benefits}
                                        onChange={e => setNewDua({ ...newDua, benefits: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        placeholder="Virtues of this dua..."
                                        rows={3}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-8">
                                <button
                                    onClick={() => setShowAddDuaModal(false)}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddDuaSubmit}
                                    className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
                                >
                                    {isEditing ? 'Update Dua' : 'Save Dua'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <Footer />
        </div>
    );
};

export default Duas;